import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalProducts: 0,
    todayRevenue: 0,
    todayOrders: 0,
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [statsRes, ordersRes, productsRes] = await Promise.allSettled([
        api.get("/admin/stats"),
        api.get("/admin/orders/recent"),
        api.get("/admin/products/top"),
      ]);

      if (statsRes.status === "fulfilled") {
        setStats(statsRes.value.data);
      }

      if (ordersRes.status === "fulfilled" && Array.isArray(ordersRes.value.data)) {
        setRecentOrders(ordersRes.value.data.slice(0, 5));
      } else {
        setRecentOrders([]);
      }

      if (productsRes.status === "fulfilled" && Array.isArray(productsRes.value.data)) {
        setTopProducts(productsRes.value.data);
      } else {
        setTopProducts([]);
      }
    } catch (error) {
      console.error("❌ Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user.isAdmin && user.role !== "manager") {
      navigate("/");
      return;
    }
    fetchDashboardData();
  }, [navigate, fetchDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + "₫";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Đã giao": return "bg-green-50 text-green-600 border-green-200";
      case "Đang giao": return "bg-blue-50 text-blue-600 border-blue-200";
      case "Đã xác nhận": return "bg-indigo-50 text-indigo-600 border-indigo-200";
      case "Chờ xác nhận":
      case "Chờ thanh toán": return "bg-yellow-50 text-yellow-600 border-yellow-200";
      case "Đã hủy": return "bg-red-50 text-red-600 border-red-200";
      default: return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mx-auto"></div>
      </div>
    );
  }

  const statCards = [
    { label: "Tổng doanh thu", value: formatCurrency(stats.totalRevenue), icon: "payments", color: "text-green-600", bg: "bg-green-50", trend: "+12.5%" },
    { label: "Tổng đơn hàng", value: stats.totalOrders, icon: "inventory_2", color: "text-blue-600", bg: "bg-blue-50", trend: "+8.2%" },
    { label: "Tổng người dùng", value: stats.totalUsers, icon: "group", color: "text-purple-600", bg: "bg-purple-50", trend: "+15.3%" },
    { label: "Tổng sản phẩm", value: stats.totalProducts, icon: "package_2", color: "text-orange-600", bg: "bg-orange-50", trend: "+5.1%" },
  ];

  return (
    <div className="p-3 sm:p-6 lg:p-8 bg-gray-50 min-h-full">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-3xl font-black text-slate-800">Bảng điều khiển</h1>
        <p className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider mt-1">Hệ thống quản trị TaHaShoes</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        {statCards.map((card, idx) => (
          <div key={idx} className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 border border-gray-100 hover:border-blue-200 transition-all group overflow-hidden relative">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="order-2 sm:order-1">
                <p className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-widest leading-none mb-1.5">{card.label}</p>
                <p className="text-sm sm:text-2xl font-black text-slate-800 truncate">{card.value}</p>
              </div>
              <div className={`${card.bg} ${card.color} p-2 sm:p-3 rounded-xl sm:rounded-2xl shrink-0 order-1 sm:order-2 self-start sm:self-center`}>
                <span className="material-symbols-outlined text-[20px] sm:text-[28px]">{card.icon}</span>
              </div>
            </div>
            <div className="mt-2 sm:mt-4 flex items-center text-[9px] sm:text-[11px] font-bold">
              <span className={`${card.color} bg-white px-1.5 py-0.5 rounded border border-current mr-2`}>{card.trend}</span>
              <span className="text-slate-400">vs tháng trước</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-50 flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-black text-slate-800 uppercase tracking-wide">Đơn hàng mới nhất</h2>
            <Link to="/admin/orders" className="text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-xs font-black transition-all">
              Tất cả →
            </Link>
          </div>

          <div className="overflow-x-auto overflow-y-hidden no-scrollbar">
            {recentOrders.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50/50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Đơn hàng</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">Khách háng</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Tổng tiền</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Trạng thái</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Ngày</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentOrders.map((order, index) => (
                    <tr key={order._id || index} className="hover:bg-blue-50/10 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link to="/admin/orders" className="text-xs sm:text-sm font-black text-blue-600 hover:underline">
                          #{order._id?.toString().slice(-8)}
                        </Link>
                        <p className="text-[10px] text-slate-400 font-bold block sm:hidden truncate max-w-[80px]">{order.customerName}</p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap hidden sm:table-cell">
                        <span className="text-xs sm:text-sm font-bold text-slate-700">{order.customerName}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-black text-slate-900">{formatCurrency(order.total)}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-[9px] sm:text-[10px] font-black rounded-lg border uppercase tracking-wider ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-[10px] sm:text-xs font-bold text-slate-400 hidden md:table-cell">
                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString("vi-VN") : "---"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center">
                <span className="material-symbols-outlined text-[48px] text-slate-200 mb-2">inbox</span>
                <p className="text-xs font-bold text-slate-400">Chưa có dữ liệu đơn hàng</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-50">
            <h2 className="text-base sm:text-lg font-black text-slate-800 uppercase tracking-wide">Top sản phẩm</h2>
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            {topProducts.length > 0 ? (
              topProducts.map((product) => (
                <div key={product._id || product.id} className="flex items-center gap-3 group">
                  <div className="relative shrink-0">
                    <img
                      src={product?.image || "https://placehold.co/100x100?text=No+Image"}
                      alt={product?.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover bg-gray-50 border border-gray-100 group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-black flex items-center justify-center rounded-full border border-white">
                      {product.sales || 0}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] sm:text-xs font-black text-slate-800 truncate leading-tight mb-0.5">{product.name}</p>
                    <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">{product.category}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs sm:text-sm font-black text-blue-600 leading-none mb-0.5">{formatCurrency(product.revenue || 0)}</p>
                    <p className="text-[9px] font-bold text-green-600">Đã bán {product.sales || 0}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-10 text-slate-300">
                <span className="material-symbols-outlined text-[40px]">inventory_2</span>
                <p className="text-[10px] font-bold uppercase mt-2">N/A</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
